import { EchangesBloc } from '../boite-reception/EchangesBloc.js';
import { EspaceDuContact } from '../espace-collectionneur/EspaceDuContact.js';
import { EvenementsContact } from '../evenements/EvenementsLies.js';
import { EntreeNouvelleAffaire } from '../pipeline/EntreesPipeline.js';
import { OngletsContact } from '../profils/GoutsContact.js';
import { NouvelleSelection } from '../presentation/NouvelleSelection.js';
import { LocationsContact } from '../locations/LocationsLiees.js';
import { useSalles } from '../../api/viewingRooms.js';
import { EtatDeSalle, texteSalle } from '../viewing-rooms/salleUi.js';
import { formatDate } from '@artosera/shared';
import { Badge, Button, ButtonLink, Dialog, Input, Select, Textarea } from '@artosera/ui';
import {
  LANGUES,
  ORIGINES_CONTACT,
  type Langue,
  type OrigineContact,
  type TypeContact,
} from '@artosera/shared';
import { Download, Mail, Phone, Plus, Search, X } from 'lucide-react';
import type { JSX, KeyboardEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import {
  contactsApi,
  useArchiverContact,
  useRestaurerContact,
  useContact,
  useContacts,
  useFusionnerContact,
  useModifierContact,
  usePoserConsentementContact,
  useRemplacerEtiquettesContact,
  useSupprimerContactRgpd,
  type CorpsAdresse,
  type FicheContact,
  type GenreContact,
} from '../../api/contacts.js';
import { DossiersOeuvresSection } from './DossiersOeuvresSection.js';
import { estErreurStepUpRequis } from '../../api/coffre.js';
import { documentsGalerieApi } from '../../api/documentsGalerie.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule, galerieEcritModule } from '../../auth/sujet.js';
import type { GalerieSession } from '../../api/types.js';
import { useShellContext } from '../shell/context.js';
import { useOptionsTypeContact } from '../../api/typesContact.js';
import { StepUpDialog } from '../coffre/StepUpDialog.js';
import { Field } from '../Field.js';
import { RetourGeste } from '../RetourGeste.js';
import { GardeSaisie } from '../GardeSaisie.js';
import { useErreurFormulaire } from '../useErreurFormulaire.js';
import { LIMITES, erreurEmail, erreurTelephone, erreurTvaIntracom } from '../validations.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { estStatutOeuvre, PastilleStatut } from '../../composants/PastilleStatut.js';
import { Monogramme } from '../../composants/Monogramme.js';
import { HistoriqueContact } from '../offres/HistoriqueContact.js';
import styles from './ContactDetailPage.module.css';

const ADRESSE_VIDE: CorpsAdresse = {
  libelle: '',
  ligne1: '',
  ligne2: '',
  codePostal: '',
  ville: '',
  pays: 'FR',
};

/** Fiche contact complète (3.3, écran 3, S3-c). */
export function ContactDetailPage(): JSX.Element {
  const { t } = useTranslation(['regie', 'contacts', 'commun']);
  const { id } = useParams<{ id: string }>();
  const { galerieSlug, galerie } = useShellContext();

  const fiche = useContact(galerieSlug, id ?? null);

  if (fiche.isLoading) return <p className={styles.vide}>{t('commun:chargement')}</p>;
  if (!fiche.data) {
    return (
      <p className={styles.vide} role="alert">
        {estApiError(fiche.error) ? fiche.error.message : t('commun:erreurReseau')}
      </p>
    );
  }

  return (
    <FicheContactVue
      // Remonte à chaque changement de contact ou après la suppression RGPD
      // (l'anonymisation change la forme de la fiche) : formulaire toujours
      // reparti des vraies valeurs, sans effet de synchronisation.
      key={`${fiche.data.contact.id}-${fiche.data.contact.anonymise ? 'anonymise' : 'actif'}`}
      galerieSlug={galerieSlug}
      galerie={galerie}
      donnees={fiche.data}
    />
  );
}

interface FicheContactProps {
  galerieSlug: string;
  galerie: GalerieSession;
  donnees: FicheContact;
}

function FicheContactVue({ galerieSlug, galerie, donnees }: FicheContactProps): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'contacts', 'commun']);
  const langueUi = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const { contact, agregats, demandes, reservations } = donnees;

  const peutEcrire = galerieALaction(galerie, 'contacts:write');
  const peutExporter = galerieALaction(galerie, 'contacts:export');
  const peutSupprimer = galerieALaction(galerie, 'contacts:erase');

  // Tous les Hooks avant toute sortie conditionnelle (règle des Hooks) : la
  // branche « contact anonymisé » ci-dessous n'en a pas besoin, mais
  // `FicheContactVue` doit les appeler dans le même ordre à chaque rendu.
  const typesContact = useOptionsTypeContact(galerieSlug);
  const modifier = useModifierContact(galerieSlug);
  const etiquettes = useRemplacerEtiquettesContact(galerieSlug);
  const consentement = usePoserConsentementContact(galerieSlug);
  const archiver = useArchiverContact(galerieSlug);
  const restaurer = useRestaurerContact(galerieSlug);
  const supprimer = useSupprimerContactRgpd(galerieSlug);

  const [genre, setGenre] = useState<GenreContact>(contact.genre);
  const [nom, setNom] = useState(contact.nom);
  const [prenom, setPrenom] = useState(contact.prenom ?? '');
  const [raisonSociale, setRaisonSociale] = useState(contact.raisonSociale ?? '');
  const [type, setType] = useState<TypeContact>(contact.type);
  const [langue, setLangue] = useState<Langue>(contact.langue);
  const [numeroTva, setNumeroTva] = useState(contact.numeroTvaIntracom ?? '');
  const [notes, setNotes] = useState(contact.notes);
  const [emails, setEmails] = useState<string[]>([...contact.emails]);
  const [telephones, setTelephones] = useState<string[]>([...contact.telephones]);
  const [adresses, setAdresses] = useState<CorpsAdresse[]>(
    contact.adresses.length > 0 ? contact.adresses.map((adresse) => ({ ...adresse })) : [],
  );
  const formulaire = useErreurFormulaire();
  const [succes, setSucces] = useState(false);
  // Garde de saisie : ce qui est enregistré fait foi ; tout écart avec la saisie
  // courante est une modification non enregistrée.
  const saisie = JSON.stringify([
    genre,
    nom,
    prenom,
    raisonSociale,
    type,
    langue,
    numeroTva,
    notes,
    emails,
    telephones,
    adresses,
  ]);
  const [saisieEnregistree, setSaisieEnregistree] = useState(saisie);
  const modifie = saisie !== saisieEnregistree;
  const erreurTva = numeroTva.trim() ? erreurTvaIntracom(numeroTva) : null;
  const erreursEmails = emails.map((valeur) => (valeur.trim() ? erreurEmail(valeur) : null));
  const erreursTelephones = telephones.map((valeur) =>
    valeur.trim() ? erreurTelephone(valeur) : null,
  );
  const saisieValide =
    !erreurTva && !erreursEmails.some(Boolean) && !erreursTelephones.some(Boolean);
  const [retourGeste, setRetourGeste] = useState<string | null>(null);

  const [nouvelleEtiquette, setNouvelleEtiquette] = useState('');
  const [sourceConsentement, setSourceConsentement] = useState<OrigineContact>('regie');

  const [archivageOuvert, setArchivageOuvert] = useState(false);
  const [fusionOuverte, setFusionOuverte] = useState(false);
  const [suppressionOuverte, setSuppressionOuverte] = useState(false);
  const [raisonSuppression, setRaisonSuppression] = useState('');
  const [erreurSuppression, setErreurSuppression] = useState<string | null>(null);
  // L'effacement exige une vérification récente du second facteur : la
  // confirmation s'efface le temps de la vérification, puis le geste est rejoué.
  const [elevationSuppression, setElevationSuppression] = useState(false);
  const [elevationExport, setElevationExport] = useState(false);
  const [elevationExportPdf, setElevationExportPdf] = useState(false);
  const [exportEnCours, setExportEnCours] = useState(false);
  const [exportPdfEnCours, setExportPdfEnCours] = useState(false);
  const [erreurExport, setErreurExport] = useState<string | null>(null);
  const [lienExport, setLienExport] = useState<string | null>(null);

  if (contact.anonymise) {
    return (
      <div className={styles.page}>
        <Link to={`/${galerieSlug}/contacts`} className={styles.retour}>
          ← {t('contacts:titre')}
        </Link>
        <div className={styles.bandeauSupprime}>
          <Monogramme nom="" taille={72} />
          <h1 className={styles.titre}>{t('regie:contacts.contactSupprime')}</h1>
          <p>{t('contacts:rgpd.anonymise')}</p>
          {contact.anonymiseLe ? (
            <p className={styles.aide}>
              {t('regie:contacts.anonymiseLe', {
                date: formatDate(new Date(contact.anonymiseLe), langueUi, 'court'),
              })}
            </p>
          ) : null}
        </div>
        <AgregatsEtHistorique
          galerieSlug={galerieSlug}
          agregats={agregats}
          demandes={demandes}
          reservations={reservations}
          langueUi={langueUi}
        />
      </div>
    );
  }

  async function enregistrer(): Promise<void> {
    if (!saisieValide) return;
    formulaire.reinitialiser();
    setSucces(false);
    try {
      await modifier.mutateAsync({
        id: contact.id,
        corps: {
          genre,
          nom: genre === 'organisation' ? raisonSociale.trim() || nom.trim() : nom.trim(),
          ...(prenom.trim() ? { prenom: prenom.trim() } : {}),
          ...(genre === 'organisation' ? { raisonSociale: raisonSociale.trim() } : {}),
          type,
          langue,
          ...(numeroTva.trim() ? { numeroTvaIntracom: numeroTva.trim() } : {}),
          notes,
          emails: emails.map((valeur) => valeur.trim()).filter(Boolean),
          telephones: telephones.map((valeur) => valeur.trim()).filter(Boolean),
          adresses,
        },
      });
      setSaisieEnregistree(saisie);
      setSucces(true);
    } catch (error) {
      formulaire.appliquer(error, [
        'nom',
        'prenom',
        'raisonSociale',
        'numeroTvaIntracom',
        'emails',
        'telephones',
        'adresses',
        'notes',
      ]);
    }
  }

  function ajouterEtiquette(): void {
    const valeur = nouvelleEtiquette.trim();
    if (!valeur || contact.etiquettes.includes(valeur)) {
      setNouvelleEtiquette('');
      return;
    }
    etiquettes.mutate({ id: contact.id, etiquettes: [...contact.etiquettes, valeur] });
    setNouvelleEtiquette('');
  }

  function retirerEtiquette(valeur: string): void {
    etiquettes.mutate({
      id: contact.id,
      etiquettes: contact.etiquettes.filter((etiquette) => etiquette !== valeur),
    });
  }

  async function exporter(apresVerification: boolean): Promise<void> {
    setErreurExport(null);
    setExportEnCours(true);
    try {
      const dossier = await dansLaPorteeCourante((appel) =>
        contactsApi.exporter(galerieSlug, contact.id, appel),
      );
      const blob = new Blob([JSON.stringify(dossier, null, 2)], { type: 'application/json' });
      setLienExport(URL.createObjectURL(blob));
    } catch (error) {
      if (estErreurStepUpRequis(error) && !apresVerification) setElevationExport(true);
      else setErreurExport(estApiError(error) ? error.message : t('commun:erreurReseau'));
    } finally {
      setExportEnCours(false);
    }
  }

  async function exporterPdf(apresVerification: boolean): Promise<void> {
    setErreurExport(null);
    setExportPdfEnCours(true);
    try {
      const document = await dansLaPorteeCourante((appel) =>
        documentsGalerieApi.exportContactPdf(galerieSlug, contact.id, langueUi, appel),
      );
      ouvrirUrlSignee(document.url);
    } catch (error) {
      if (estErreurStepUpRequis(error) && !apresVerification) setElevationExportPdf(true);
      else setErreurExport(estApiError(error) ? error.message : t('commun:erreurReseau'));
    } finally {
      setExportPdfEnCours(false);
    }
  }

  /** `apresVerification` : un nouveau refus d'élévation se dit, sans rouvrir la boîte en boucle. */
  async function confirmerSuppression(apresVerification = false): Promise<void> {
    setErreurSuppression(null);
    try {
      await supprimer.mutateAsync({ id: contact.id, raison: raisonSuppression });
      setSuppressionOuverte(false);
    } catch (error) {
      if (estErreurStepUpRequis(error) && !apresVerification) setElevationSuppression(true);
      else setErreurSuppression(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  return (
    <div className={styles.page}>
      <GardeSaisie actif={modifie && peutEcrire} />
      <Link to={`/${galerieSlug}/contacts`} className={styles.retour}>
        ← {t('contacts:titre')}
      </Link>

      {galerieAModule(galerie, 'modeles-contrats') &&
        galerieALaction(galerie, 'contracts:write') && (
          <details className={styles.genererContrat}>
            <summary>{t('regie:contacts.genererContrat.menu')}</summary>
            {(['nda', 'depot'] as const).map((genre) => (
              <p key={genre}>
                <Link
                  to={`/${galerieSlug}/contrats?nouveau=1&genre=${genre}&contactId=${contact.id}`}
                >
                  {t(`regie:contacts.genererContrat.${genre}`)}
                </Link>
              </p>
            ))}
          </details>
        )}
      <EntreeNouvelleAffaire contactInitial={{ id: contact.id, nom: contact.nomAffiche }} />
      <EchangesBloc contact={contact} />
      <header className={styles.carte}>
        <Monogramme nom={contact.nomAffiche} taille={72} />
        <div className={styles.carteIdentite}>
          <NouvelleSelection
            titreInitial={contact.nomAffiche}
            origine={{ depuis: { contact: contact.id } }}
          />
          <h1 className={styles.titre}>{contact.nomAffiche}</h1>
          <p className={styles.carteSousTitre}>
            <span>{typesContact.libelle(contact.type)}</span>
            {contact.adresses[0]?.ville ? <span>{contact.adresses[0].ville}</span> : null}
            <span>{t(`commun:langues.${contact.langue}`)}</span>
          </p>
          {contact.emails[0] || contact.telephones[0] ? (
            <p className={styles.carteCoordonnees}>
              {contact.emails[0] ? (
                <span className={styles.carteLigne}>
                  <Mail size={14} strokeWidth={1.5} aria-hidden />
                  {contact.emails[0]}
                </span>
              ) : null}
              {contact.telephones[0] ? (
                <span className={styles.carteLigne}>
                  <Phone size={14} strokeWidth={1.5} aria-hidden />
                  {contact.telephones[0]}
                </span>
              ) : null}
            </p>
          ) : null}
        </div>
        {peutEcrire ? (
          <div className={styles.gestes}>
            <Button variant="secondary" size="sm" onClick={() => setFusionOuverte(true)}>
              {t('regie:contacts.fusion.action')}
            </Button>
            {peutExporter ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void exporter(false)}
                loading={exportEnCours}
              >
                {t('contacts:rgpd.exporter')}
              </Button>
            ) : null}
            {peutExporter ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void exporterPdf(false)}
                loading={exportPdfEnCours}
              >
                {t('regie:contacts.export.telechargerPdf')}
              </Button>
            ) : null}
            <span className={styles.gestesSensibles}>
              {contact.archive && contact.fusionneDans !== null ? null : contact.archive ? (
                // Un doublon fondu dans un autre ne se restaure pas : l'API le
                // refuse (`deja_fusionne`), ses rattachements sont partis.
                <Button
                  variant="secondary"
                  size="sm"
                  loading={restaurer.isPending}
                  onClick={() =>
                    restaurer.mutate(contact.id, {
                      onSuccess: () => {
                        setSucces(false);
                        setRetourGeste(t('regie:contacts.restaurer.succes'));
                      },
                    })
                  }
                >
                  {t('regie:contacts.restaurer.action')}
                </Button>
              ) : (
                <Button variant="danger" size="sm" onClick={() => setArchivageOuvert(true)}>
                  {t('regie:contacts.archiver.action')}
                </Button>
              )}
              {peutSupprimer ? (
                <Button variant="danger" size="sm" onClick={() => setSuppressionOuverte(true)}>
                  {t('contacts:rgpd.supprimer')}
                </Button>
              ) : null}
            </span>
          </div>
        ) : null}
      </header>

      {erreurExport ? (
        <p className={styles.erreur} role="alert">
          {erreurExport}
        </p>
      ) : null}
      {lienExport ? (
        <p className={styles.export}>
          <ButtonLink
            variant="secondary"
            size="sm"
            href={lienExport}
            download={`contact-${contact.id}.json`}
          >
            <Download size={16} strokeWidth={1.5} aria-hidden />
            {t('regie:contacts.export.telecharger')}
          </ButtonLink>
        </p>
      ) : null}

      <OngletsContact contactId={contact.id} emails={contact.emails}>
        <div className={styles.colonnes}>
          <div className={styles.principal}>
            <section className={styles.section}>
              <h2 className={styles.sousTitre}>{t('regie:contacts.onglets.identite')}</h2>
              <div className={styles.grille2}>
                <Field
                  label={t('regie:contacts.nouveauContact.genreChamp')}
                  htmlFor="contact-fiche-genre"
                >
                  <Select
                    id="contact-fiche-genre"
                    aria-label={t('regie:contacts.nouveauContact.genreChamp')}
                    value={genre}
                    disabled={!peutEcrire}
                    onValueChange={(valeur) => setGenre(valeur as GenreContact)}
                    options={[
                      {
                        value: 'personne',
                        label: t('regie:contacts.nouveauContact.genrePersonne'),
                      },
                      {
                        value: 'organisation',
                        label: t('regie:contacts.nouveauContact.genreOrganisation'),
                      },
                    ]}
                  />
                </Field>
                <Field
                  label={t('regie:contacts.nouveauContact.typeChamp')}
                  htmlFor="contact-fiche-type"
                >
                  <Select
                    id="contact-fiche-type"
                    aria-label={t('regie:contacts.nouveauContact.typeChamp')}
                    value={type}
                    disabled={!peutEcrire}
                    onValueChange={(valeur) => setType(valeur as TypeContact)}
                    options={typesContact.options}
                  />
                </Field>
              </div>

              {genre === 'organisation' ? (
                <Field
                  label={t('regie:contacts.nouveauContact.raisonSocialeChamp')}
                  htmlFor="contact-fiche-raison"
                >
                  <Input
                    id="contact-fiche-raison"
                    disabled={!peutEcrire}
                    value={raisonSociale}
                    onChange={(event) => setRaisonSociale(event.target.value)}
                  />
                </Field>
              ) : null}

              <div className={styles.grille2}>
                <Field
                  label={t('regie:contacts.nouveauContact.nomChamp')}
                  htmlFor="contact-fiche-nom"
                >
                  <Input
                    id="contact-fiche-nom"
                    disabled={!peutEcrire}
                    value={nom}
                    onChange={(event) => setNom(event.target.value)}
                  />
                </Field>
                <Field
                  label={t('regie:contacts.nouveauContact.prenomChamp')}
                  htmlFor="contact-fiche-prenom"
                >
                  <Input
                    id="contact-fiche-prenom"
                    disabled={!peutEcrire}
                    value={prenom}
                    onChange={(event) => setPrenom(event.target.value)}
                  />
                </Field>
              </div>

              <div className={styles.grille2}>
                <Field label={t('regie:contacts.langueChamp')} htmlFor="contact-fiche-langue">
                  <Select
                    id="contact-fiche-langue"
                    aria-label={t('regie:contacts.langueChamp')}
                    value={langue}
                    disabled={!peutEcrire}
                    onValueChange={(valeur) => setLangue(valeur as Langue)}
                    options={LANGUES.map((code) => ({
                      value: code,
                      label: t(`commun:langues.${code}`),
                    }))}
                  />
                </Field>
                <Field
                  label={t('regie:contacts.numeroTvaChamp')}
                  htmlFor="contact-fiche-tva"
                  error={erreurTva ? t(erreurTva) : formulaire.messageChamp('numeroTvaIntracom')}
                >
                  <Input
                    id="contact-fiche-tva"
                    mono
                    maxLength={LIMITES.contact.tva}
                    disabled={!peutEcrire}
                    value={numeroTva}
                    onChange={(event) => setNumeroTva(event.target.value)}
                  />
                </Field>
              </div>

              <p className={styles.aide}>{t(`contacts:origines.${contact.origine}`)}</p>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sousTitre}>{t('regie:contacts.onglets.coordonnees')}</h2>
              <ListeValeurs
                idPrefixe="contact-fiche-email"
                type="email"
                label={t('regie:contacts.emailsLabel')}
                ajouterLabel={t('regie:contacts.ajouterEmail')}
                retirerLabel={t('regie:contacts.retirerEmail')}
                valeurs={emails}
                erreurs={erreursEmails.map((cle) => (cle ? t(cle) : null))}
                maxLength={LIMITES.contact.email}
                disabled={!peutEcrire}
                onChange={setEmails}
              />
              <ListeValeurs
                idPrefixe="contact-fiche-telephone"
                type="tel"
                label={t('regie:contacts.telephonesLabel')}
                ajouterLabel={t('regie:contacts.ajouterTelephone')}
                retirerLabel={t('regie:contacts.retirerTelephone')}
                valeurs={telephones}
                erreurs={erreursTelephones.map((cle) => (cle ? t(cle) : null))}
                maxLength={LIMITES.contact.telephone}
                disabled={!peutEcrire}
                onChange={setTelephones}
              />

              <div>
                <p className={styles.aide}>{t('regie:contacts.adressesLabel')}</p>
                <div className={styles.listeValeurs}>
                  {adresses.map((adresse, index) => (
                    // Pas d'identifiant stable côté client avant enregistrement.
                    <div key={index} className={styles.blocAdresse}>
                      <div className={styles.blocAdresseEntete}>
                        <Input
                          aria-label={t('regie:contacts.adresse.libelle')}
                          placeholder={t('regie:contacts.adresse.libelle')}
                          disabled={!peutEcrire}
                          value={adresse.libelle ?? ''}
                          onChange={(event) =>
                            setAdresses((valeurs) =>
                              valeurs.map((item, i) =>
                                i === index ? { ...item, libelle: event.target.value } : item,
                              ),
                            )
                          }
                        />
                        {peutEcrire ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setAdresses((valeurs) => valeurs.filter((_, i) => i !== index))
                            }
                          >
                            <X size={14} strokeWidth={1.5} aria-hidden />
                            {t('commun:actions.supprimer')}
                          </Button>
                        ) : null}
                      </div>
                      <Input
                        aria-label={t('regie:contacts.adresse.ligne1')}
                        placeholder={t('regie:contacts.adresse.ligne1')}
                        disabled={!peutEcrire}
                        value={adresse.ligne1 ?? ''}
                        onChange={(event) =>
                          setAdresses((valeurs) =>
                            valeurs.map((item, i) =>
                              i === index ? { ...item, ligne1: event.target.value } : item,
                            ),
                          )
                        }
                      />
                      <Input
                        aria-label={t('regie:contacts.adresse.ligne2')}
                        placeholder={t('regie:contacts.adresse.ligne2')}
                        disabled={!peutEcrire}
                        value={adresse.ligne2 ?? ''}
                        onChange={(event) =>
                          setAdresses((valeurs) =>
                            valeurs.map((item, i) =>
                              i === index ? { ...item, ligne2: event.target.value } : item,
                            ),
                          )
                        }
                      />
                      <Input
                        aria-label={t('regie:contacts.adresse.codePostal')}
                        placeholder={t('regie:contacts.adresse.codePostal')}
                        disabled={!peutEcrire}
                        value={adresse.codePostal ?? ''}
                        onChange={(event) =>
                          setAdresses((valeurs) =>
                            valeurs.map((item, i) =>
                              i === index ? { ...item, codePostal: event.target.value } : item,
                            ),
                          )
                        }
                      />
                      <Input
                        aria-label={t('regie:contacts.adresse.ville')}
                        placeholder={t('regie:contacts.adresse.ville')}
                        disabled={!peutEcrire}
                        value={adresse.ville ?? ''}
                        onChange={(event) =>
                          setAdresses((valeurs) =>
                            valeurs.map((item, i) =>
                              i === index ? { ...item, ville: event.target.value } : item,
                            ),
                          )
                        }
                      />
                      <Input
                        aria-label={t('regie:contacts.adresse.pays')}
                        placeholder={t('regie:contacts.adresse.pays')}
                        mono
                        maxLength={2}
                        disabled={!peutEcrire}
                        value={adresse.pays ?? ''}
                        onChange={(event) =>
                          setAdresses((valeurs) =>
                            valeurs.map((item, i) =>
                              i === index
                                ? { ...item, pays: event.target.value.toUpperCase() }
                                : item,
                            ),
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
                {peutEcrire ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAdresses((valeurs) => [...valeurs, { ...ADRESSE_VIDE }])}
                  >
                    <Plus size={14} strokeWidth={1.5} aria-hidden />
                    {t('regie:contacts.ajouterAdresse')}
                  </Button>
                ) : null}
              </div>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sousTitre}>{t('regie:contacts.onglets.etiquettes')}</h2>
              <div className={styles.etiquettes}>
                {contact.etiquettes.map((valeur) => (
                  <button
                    key={valeur}
                    type="button"
                    className={styles.etiquette}
                    disabled={!peutEcrire}
                    onClick={() => retirerEtiquette(valeur)}
                    aria-label={t('regie:contacts.retirerEtiquette', { etiquette: valeur })}
                  >
                    {valeur}
                    <X size={12} strokeWidth={1.5} aria-hidden />
                  </button>
                ))}
                {peutEcrire ? (
                  <input
                    type="text"
                    className={styles.saisieEtiquette}
                    placeholder={t('regie:contacts.ajouterEtiquette')}
                    aria-label={t('regie:contacts.ajouterEtiquette')}
                    value={nouvelleEtiquette}
                    onChange={(event) => setNouvelleEtiquette(event.target.value)}
                    onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        ajouterEtiquette();
                      }
                    }}
                  />
                ) : null}
              </div>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sousTitre}>{t('contacts:consentement.titre')}</h2>
              <div className={styles.consentementLigne}>
                <div>
                  <Badge tone={contact.consentementNewsletter.accorde ? 'available' : 'neutral'}>
                    {contact.consentementNewsletter.accorde
                      ? t('contacts:consentement.titre')
                      : t('regie:contacts.consentementNon')}
                  </Badge>
                  <p className={styles.consentementTexte}>
                    {contact.consentementNewsletter.accorde && contact.consentementNewsletter.date
                      ? t('contacts:consentement.accorde', {
                          date: formatDate(
                            new Date(contact.consentementNewsletter.date),
                            langueUi,
                            'court',
                          ),
                        })
                      : contact.consentementNewsletter.retireLe
                        ? t('contacts:consentement.retire', {
                            date: formatDate(
                              new Date(contact.consentementNewsletter.retireLe),
                              langueUi,
                              'court',
                            ),
                          })
                        : t('contacts:consentement.refuse')}
                    {contact.consentementNewsletter.source
                      ? ` — ${t(`contacts:origines.${contact.consentementNewsletter.source}`)}`
                      : ''}
                  </p>
                </div>
                {peutEcrire ? (
                  contact.consentementNewsletter.accorde ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={consentement.isPending}
                      onClick={() => consentement.mutate({ id: contact.id, accorde: false })}
                    >
                      {t('regie:contacts.consentementRetirer')}
                    </Button>
                  ) : (
                    <div className={styles.gestes}>
                      <Select
                        aria-label={t('regie:contacts.consentementSource')}
                        value={sourceConsentement}
                        onValueChange={(valeur) => setSourceConsentement(valeur as OrigineContact)}
                        options={ORIGINES_CONTACT.map((code) => ({
                          value: code,
                          label: t(`contacts:origines.${code}`),
                        }))}
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={consentement.isPending}
                        onClick={() =>
                          consentement.mutate({
                            id: contact.id,
                            accorde: true,
                            source: sourceConsentement,
                          })
                        }
                      >
                        {t('regie:contacts.consentementAccorder')}
                      </Button>
                    </div>
                  )
                ) : null}
              </div>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sousTitre}>{t('regie:contacts.onglets.notes')}</h2>
              <Textarea
                rows={4}
                aria-label={t('regie:contacts.onglets.notes')}
                disabled={!peutEcrire}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </section>

            {peutEcrire || formulaire.erreur || succes || retourGeste ? (
              <div className={styles.barre}>
                <div className={styles.barreMessage}>
                  {formulaire.erreur ? (
                    <p className={styles.erreur} role="alert">
                      {formulaire.erreur}
                    </p>
                  ) : null}
                  {succes ? (
                    <p className={styles.succes} role="status">
                      {t('contacts:messages.modifie')}
                    </p>
                  ) : null}
                  <RetourGeste message={retourGeste} />
                </div>
                {peutEcrire ? (
                  <Button onClick={() => void enregistrer()} loading={modifier.isPending}>
                    {t('commun:actions.enregistrer')}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>

          <aside className={styles.secondaire}>
            {!contact.anonymise &&
              galerieAModule(galerie, 'espace-collectionneur') &&
              galerieALaction(galerie, 'collector-space:read') && (
                <EspaceDuContact contactId={contact.id} emails={contact.emails} />
              )}
            {!contact.anonymise &&
              galerieAModule(galerie, 'documents-prestige') &&
              galerieALaction(galerie, 'documents:read') && (
                <DossiersOeuvresSection
                  galerieSlug={galerieSlug}
                  contactId={contact.id}
                  contactNom={contact.nomAffiche}
                  peutEcrire={
                    galerieALaction(galerie, 'documents:write') &&
                    galerieEcritModule(galerie, 'documents-prestige')
                  }
                />
              )}
            {galerieAModule(galerie, 'evenements') && galerieALaction(galerie, 'events:read') && (
              <EvenementsContact contactId={contact.id} />
            )}
            {galerieAModule(galerie, 'location-oeuvres') &&
              galerieALaction(galerie, 'locations:read') && (
                <LocationsContact galerieSlug={galerieSlug} contactId={contact.id} />
              )}
            {galerieAModule(galerie, 'viewing-rooms') &&
              galerieALaction(galerie, 'viewing-rooms:read') && (
                <SallesDuContact galerieSlug={galerieSlug} contactId={contact.id} />
              )}
            <AgregatsEtHistorique
              galerieSlug={galerieSlug}
              agregats={agregats}
              demandes={demandes}
              reservations={reservations}
              langueUi={langueUi}
            />
            {galerieAModule(galerie, 'offres-relances') &&
            galerieALaction(galerie, 'offers:read') ? (
              <HistoriqueContact
                galerieSlug={galerieSlug}
                contactId={contact.id}
                contactNom={contact.nomAffiche}
              />
            ) : null}
          </aside>
        </div>
      </OngletsContact>

      <Dialog
        open={archivageOuvert}
        onOpenChange={setArchivageOuvert}
        title={t('regie:contacts.archiver.confirmationTitre')}
        description={t('regie:contacts.archiver.confirmationTexte')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setArchivageOuvert(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              variant="danger"
              loading={archiver.isPending}
              onClick={() => {
                archiver.mutate(
                  { id: contact.id },
                  {
                    onSuccess: () => {
                      setArchivageOuvert(false);
                      setSucces(false);
                      setRetourGeste(t('regie:contacts.archiver.succes'));
                    },
                  },
                );
              }}
            >
              {t('regie:contacts.archiver.action')}
            </Button>
          </>
        }
      >
        <p>{contact.nomAffiche}</p>
      </Dialog>

      <FusionnerDialog
        galerieSlug={galerieSlug}
        contact={contact}
        open={fusionOuverte}
        onOpenChange={setFusionOuverte}
        onFusionne={(idConserve) => navigate(`/${galerieSlug}/contacts/${idConserve}`)}
      />

      <Dialog
        open={suppressionOuverte && !elevationSuppression}
        onOpenChange={(ouvert) => {
          setSuppressionOuverte(ouvert);
          if (!ouvert) {
            setRaisonSuppression('');
            setErreurSuppression(null);
          }
        }}
        title={t('contacts:rgpd.supprimer')}
        description={t('regie:contacts.suppression.description')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setSuppressionOuverte(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              variant="danger"
              loading={supprimer.isPending}
              disabled={raisonSuppression.trim().length < 3}
              onClick={() => void confirmerSuppression()}
            >
              {t('regie:contacts.suppression.confirmer')}
            </Button>
          </>
        }
      >
        <Field
          label={t('regie:contacts.suppression.raisonChamp')}
          htmlFor="contact-suppression-raison"
        >
          <Textarea
            id="contact-suppression-raison"
            required
            rows={3}
            value={raisonSuppression}
            onChange={(event) => setRaisonSuppression(event.target.value)}
          />
        </Field>
        {erreurSuppression ? (
          <p className={styles.erreur} role="alert">
            {erreurSuppression}
          </p>
        ) : null}
      </Dialog>
      <StepUpDialog
        open={elevationExport}
        onOpenChange={setElevationExport}
        onVerifie={() => void exporter(true)}
        description={t('regie:contacts.export.elevation')}
      />
      <StepUpDialog
        open={elevationExportPdf}
        onOpenChange={setElevationExportPdf}
        onVerifie={() => void exporterPdf(true)}
        description={t('regie:contacts.export.elevation')}
      />
      <StepUpDialog
        open={elevationSuppression}
        onOpenChange={(ouvert) => {
          if (!ouvert) setElevationSuppression(false);
        }}
        onVerifie={() => void confirmerSuppression(true)}
        description={t('regie:contacts.suppression.elevation')}
      />
    </div>
  );
}

interface ListeValeursProps {
  idPrefixe: string;
  type: 'email' | 'tel';
  label: string;
  ajouterLabel: string;
  retirerLabel: string;
  valeurs: readonly string[];
  /** Un message par valeur (ou `null`), affiché sous le champ concerné. */
  erreurs?: readonly (string | null)[];
  maxLength?: number;
  disabled: boolean;
  onChange: (valeurs: string[]) => void;
}

/** Éditeur d'une liste de valeurs simples (e-mails, téléphones) : ajout/retrait libres. */
function ListeValeurs({
  idPrefixe,
  type,
  label,
  ajouterLabel,
  retirerLabel,
  valeurs,
  erreurs,
  maxLength,
  disabled,
  onChange,
}: ListeValeursProps): JSX.Element {
  return (
    <div>
      <p className={styles.aide}>{label}</p>
      <div className={styles.listeValeurs}>
        {valeurs.map((valeur, index) => (
          <div key={`${idPrefixe}-${String(index)}`} className={styles.ligneValeur}>
            <Input
              id={index === 0 ? idPrefixe : undefined}
              aria-label={label}
              aria-invalid={erreurs?.[index] ? true : undefined}
              type={type}
              maxLength={maxLength}
              disabled={disabled}
              value={valeur}
              onChange={(event) => {
                const suivants = [...valeurs];
                suivants[index] = event.target.value;
                onChange(suivants);
              }}
            />
            {!disabled ? (
              <Button
                variant="ghost"
                size="sm"
                aria-label={retirerLabel}
                onClick={() => onChange(valeurs.filter((_, i) => i !== index))}
              >
                <X size={14} strokeWidth={1.5} aria-hidden />
              </Button>
            ) : null}
            {erreurs?.[index] ? (
              <p className={styles.erreurValeur} role="alert">
                {erreurs[index]}
              </p>
            ) : null}
          </div>
        ))}
      </div>
      {!disabled ? (
        <Button variant="ghost" size="sm" onClick={() => onChange([...valeurs, ''])}>
          <Plus size={14} strokeWidth={1.5} aria-hidden />
          {ajouterLabel}
        </Button>
      ) : null}
    </div>
  );
}

interface AgregatsEtHistoriqueProps {
  galerieSlug: string;
  agregats: FicheContact['agregats'];
  demandes: FicheContact['demandes'];
  reservations: FicheContact['reservations'];
  langueUi: 'fr' | 'en';
}

/** Agrégats de la fiche et historique des vingt dernières demandes/réservations (§ 14.2). */
function AgregatsEtHistorique({
  galerieSlug,
  agregats,
  demandes,
  reservations,
  langueUi,
}: AgregatsEtHistoriqueProps): JSX.Element {
  const { t } = useTranslation(['regie', 'contacts', 'commun', 'ventes']);

  return (
    <>
      <section className={styles.section}>
        <h2 className={styles.sousTitre}>{t('regie:contacts.onglets.agregats')}</h2>
        <div className={styles.agregats}>
          <div className={styles.agregat}>
            <span className={styles.agregatValeur}>{agregats.demandes.total}</span>
            <span className={styles.agregatLabel}>{t('contacts:demandes.titre')}</span>
          </div>
          <div className={styles.agregat}>
            <span className={styles.agregatValeur}>{agregats.demandes.aTraiter}</span>
            <span className={styles.agregatLabel}>{t('contacts:demandes.a_traiter')}</span>
          </div>
          <div className={styles.agregat}>
            <span className={styles.agregatValeur}>{agregats.reservations.total}</span>
            <span className={styles.agregatLabel}>{t('regie:contacts.reservationsTotal')}</span>
          </div>
          <div className={styles.agregat}>
            <span className={styles.agregatValeur}>{agregats.reservations.actives}</span>
            <span className={styles.agregatLabel}>{t('regie:contacts.reservationsActives')}</span>
          </div>
        </div>

        {agregats.oeuvresDInteret.length > 0 ? (
          <ul className={styles.listeOeuvres}>
            {agregats.oeuvresDInteret.map((oeuvre) => (
              <li key={oeuvre.id} className={styles.ligneHistorique}>
                <Link className={styles.titreOeuvre} to={`/${galerieSlug}/oeuvres/${oeuvre.id}`}>
                  {oeuvre.titre}
                </Link>
                {estStatutOeuvre(oeuvre.statut) ? (
                  <PastilleStatut statut={oeuvre.statut} />
                ) : (
                  <Badge tone="neutral">{oeuvre.statut}</Badge>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.aide}>{t('regie:contacts.aucunInteret')}</p>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sousTitre}>{t('regie:contacts.onglets.historique')}</h2>
        {demandes.length === 0 && reservations.length === 0 ? (
          <p className={styles.aide}>{t('regie:contacts.aucunHistorique')}</p>
        ) : (
          <>
            {demandes.length > 0 ? (
              <ul className={styles.listeHistorique}>
                {demandes.map((demande) => (
                  <li key={demande.id} className={styles.ligneHistorique}>
                    <span className={styles.historiqueSujet}>
                      {demande.sujet || t('contacts:demandes.titre')}
                    </span>
                    <Badge tone="neutral">{t(`contacts:demandes.${demande.etat}`)}</Badge>
                    <span className={styles.aide}>
                      {formatDate(new Date(demande.recueLe), langueUi, 'court')}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            {reservations.length > 0 ? (
              <ul className={styles.listeHistorique}>
                {reservations.map((reservation) => (
                  <li key={reservation.id} className={styles.ligneHistorique}>
                    <Link
                      className={styles.historiqueSujet}
                      to={`/${galerieSlug}/oeuvres/${reservation.oeuvreId}`}
                    >
                      {t('regie:contacts.reservationSur')}
                    </Link>
                    <Badge tone="neutral">
                      {t(`ventes:reservations.${reservation.etat}`, {
                        defaultValue: reservation.etat,
                      })}
                    </Badge>
                    <span className={styles.aide}>
                      {formatDate(new Date(reservation.jusquau), langueUi, 'court')}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </section>
    </>
  );
}

interface FusionnerDialogProps {
  galerieSlug: string;
  contact: FicheContact['contact'];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFusionne: (idConserve: string) => void;
}

/** Dialogue de fusion (§ 14.2) : recherche par fragment, choix du doublon à fondre dans la fiche. */
function FusionnerDialog({
  galerieSlug,
  contact,
  open,
  onOpenChange,
  onFusionne,
}: FusionnerDialogProps): JSX.Element {
  const { t } = useTranslation(['regie', 'contacts', 'commun']);
  const [recherche, setRecherche] = useState('');
  const [selectionId, setSelectionId] = useState<string | null>(null);
  const [elevation, setElevation] = useState<{ id: string; avec: string } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const resultats = useContacts(galerieSlug, recherche.trim() ? { q: recherche.trim() } : {});
  const fusionner = useFusionnerContact(galerieSlug);

  const candidats = (resultats.data?.contacts ?? []).filter(
    (candidat) => candidat.id !== contact.id && !candidat.anonymise,
  );

  const enCours = fusionner.isPending || elevation !== null;

  async function confirmer(
    geste: { id: string; avec: string },
    apresVerification: boolean,
  ): Promise<void> {
    setErreur(null);
    try {
      const resultat = await fusionner.mutateAsync(geste);
      onOpenChange(false);
      setRecherche('');
      setSelectionId(null);
      onFusionne(resultat.contact.id);
    } catch (error) {
      if (estErreurStepUpRequis(error) && !apresVerification) setElevation(geste);
      else setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  return (
    <>
      <Dialog
        open={open && elevation === null}
        onOpenChange={onOpenChange}
        title={t('regie:contacts.fusion.titre')}
        description={t('regie:contacts.fusion.description')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              variant="danger"
              disabled={!selectionId || enCours}
              loading={fusionner.isPending}
              onClick={() => {
                if (selectionId) void confirmer({ id: contact.id, avec: selectionId }, false);
              }}
            >
              {t('regie:contacts.fusion.confirmer')}
            </Button>
          </>
        }
      >
        <div className={styles.rechercheFusion}>
          <div className={styles.champRecherche}>
            <Search size={16} strokeWidth={1.5} aria-hidden />
            <input
              type="search"
              aria-label={t('regie:contacts.fusion.rechercheChamp')}
              placeholder={t('regie:contacts.fusion.rechercheChamp')}
              disabled={enCours}
              value={recherche}
              onChange={(event) => {
                setRecherche(event.target.value);
                setSelectionId(null);
              }}
            />
          </div>
          <div className={styles.resultatsFusion}>
            {candidats.length === 0 ? (
              <p className={styles.aide}>{t('regie:contacts.fusion.aucunResultat')}</p>
            ) : (
              candidats.map((candidat) => (
                <button
                  key={candidat.id}
                  type="button"
                  className={`${styles.resultatFusion} ${
                    selectionId === candidat.id ? styles.resultatFusionActif : ''
                  }`}
                  disabled={enCours}
                  onClick={() => setSelectionId(candidat.id)}
                >
                  <span>{candidat.nomAffiche}</span>
                  <span className={styles.aide}>{candidat.emails[0] ?? ''}</span>
                </button>
              ))
            )}
          </div>
          {erreur ? (
            <p className={styles.erreur} role="alert">
              {erreur}
            </p>
          ) : null}
        </div>
      </Dialog>
      <StepUpDialog
        open={elevation !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setElevation(null);
        }}
        onVerifie={() => {
          if (elevation) void confirmer(elevation, true);
        }}
        description={t('regie:contacts.fusion.elevation')}
      />
    </>
  );
}

function SallesDuContact({ galerieSlug, contactId }: { galerieSlug: string; contactId: string }) {
  const { t, i18n } = useTranslation();
  const salles = useSalles(galerieSlug, { contactId });
  return (
    <section className={styles.section} aria-label={t('regie:viewingRooms.titre')}>
      <h2 className={styles.sousTitre}>{t('regie:viewingRooms.titre')}</h2>
      {salles.isLoading && <p role="status">{t('regie:viewingRooms.chargement')}</p>}
      {salles.isError && <p role="alert">{t('regie:viewingRooms.erreurs.generique')}</p>}
      {salles.data?.salles.length === 0 && <p>{t('regie:viewingRooms.vide')}</p>}
      {salles.data?.salles.map((salle) => (
        <p key={salle.id}>
          <Link to={`/${galerieSlug}/viewing-rooms/${salle.id}`}>
            {texteSalle(salle.titre, i18n.language)}
          </Link>{' '}
          <EtatDeSalle etat={salle.etat} />
        </p>
      ))}
    </section>
  );
}
